import React, { useEffect, useMemo, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Link } from '@mui/material';
import Typography from '@mui/material/Typography';
import StarIcon from '@mui/icons-material/Star';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { ErrorIcon } from '@eposnow/ui-core';
import FeedbackSection from '../components/FeedbackSection';
import ReportOverviewTable from './ReportOverviewTable';
import { useReportOverview, ReportOverviewItem, ReportCategory } from '../../../api/reportOverview';
import { UserContext } from '../../../context/UserContext';
import GenericErrorPage from '../../settings/GenericErrorPage';
import { ReactComponent as ServerIcon } from '../../../img/ServerIcon.svg';
import { ReactComponent as UnderMaintenance } from '../../../img/underMaintenance.svg';
import { ErrorObject } from '../../../api/fetch';


const ReportOverview = () => {
  const { t } = useTranslation();
  const { locale } = useContext(UserContext);
  const navigate = useNavigate();
  const {
    isError,
    error,
    data: reportOverview,
    refetch
  } = useReportOverview<ReportOverviewItem[]>('ReportOverview');

  let learnMoreUrl = `${process.env.REACT_APP_LINK_SUPPORT
    }s/article/Data-Downloader?language=${locale.substring(0, 2)}`;
  if (locale.substring(0, 2) === 'es') {
    learnMoreUrl = `${process.env.REACT_APP_LINK_SUPPORT}s/article/Data-Downloader?language=es`;
  }

  const categories = useMemo<ReportCategory[]>(() => {
    if (!reportOverview) return [];

    const map = new Map<string, ReportCategory>();

    reportOverview.forEach((report: ReportOverviewItem) => {
      map.set(report.category.displayKey, report.category);
    });

    return Array.from(map.values());
  }, [reportOverview]);

  const FAVOURITES = 'favourites';
  const ALL = 'all';

  const [activeFilter, setActiveFilter] = useState<string>();

  const hasFavourites = useMemo(
    () => reportOverview?.some(r => r.isFavourite) ?? false,
    [reportOverview]
  );

  useEffect(() => {
    if (!activeFilter && categories.length) {
      setActiveFilter(hasFavourites ? FAVOURITES : ALL);
    }
  }, [categories, activeFilter, hasFavourites]);


  const filterButtons = useMemo(() => {
    const baseButtons = hasFavourites
      ? [FAVOURITES, ALL]
      : [ALL, FAVOURITES];

    return baseButtons;
  }, [hasFavourites]);

  const filteredReports = useMemo(() => {
    if (!reportOverview) return [];

    if (activeFilter === ALL) {
      return reportOverview;
    }

    if (activeFilter === FAVOURITES) {
      return reportOverview.filter((r) => r.isFavourite);
    }

    return reportOverview.filter(
      (r) => r.category.displayKey === activeFilter
    );
  }, [reportOverview, activeFilter]);

const showNoFavourites =
  activeFilter === FAVOURITES &&
  !hasFavourites;

  if (process.env.REACT_APP_DATA_DOWNLOADER_MAINTENANCE === 'true')
    return (
      <GenericErrorPage
        icon={<UnderMaintenance />}
        title={t('components.iconMessage.errorPage.underMaintenance')}
        description={t('components.iconMessage.errorPage.underMaintenanceDescription')}
        rightButtonText={t('components.iconMessage.errorPage.back')}
        actionRightButton={() => navigate(-1)}
      />
    );

  if (isError) {
    const status = (error as ErrorObject)?.code;

    if (status === 400) {
      return (
        <GenericErrorPage
          icon={<ErrorIcon />}
          title={t('components.iconMessage.errorPage.badRequest')}
          description={t('components.iconMessage.errorPage.noProcessRequest')}
          leftButtonText={t('components.iconMessage.errorPage.back')}
          rightButtonText={t('components.iconMessage.errorPage.retry')}
          actionLeftButton={() => navigate(-1)}
          actionRightButton={() => refetch()}
        />
      );
    }

    if (status === 429) {
      return (
        <GenericErrorPage
          icon={<ServerIcon />}
          title={t('components.iconMessage.errorPage.serverBusy')}
          description={t('components.iconMessage.errorPage.serveBusyText')}
          leftButtonText={t('components.iconMessage.errorPage.back')}
          rightButtonText={t('components.iconMessage.errorPage.retry')}
          actionLeftButton={() => navigate(-1)}
          actionRightButton={() => refetch()}
        />
      );
    }

    return (
      <GenericErrorPage
        icon={<ServerIcon />}
        title={t('components.iconMessage.errorPage.internalServerError')}
        description={t('components.iconMessage.errorPage.internalServerErrorText')}
        leftButtonText={t('components.iconMessage.errorPage.status')}
        rightButtonText={t('components.iconMessage.errorPage.back')}
        actionLeftButton={() => {
          window.location.href = `${process.env.REACT_APP_LINK_STATUS_PAGE}`;
        }}
        actionRightButton={() => navigate(-1)}
      />
    );
  }

  return (
    <>
      <FeedbackSection text={t('actions.feedbackDownloader')} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          marginX: '112px',
          marginY: '48px'
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            marginBottom: '18px',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <Typography variant="h1">
              {t('screens.dataDownloader.overviewTitle')}
            </Typography>
            <Button variant="contained" color="primary" to='/reports-hub' component={RouterLink}>
              {t('screens.dataDownloader.reportsOverviewButton')}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', marginBottom: '8px' }}>
            <Typography variant="body1">
              {t('screens.dataDownloader.overviewDescription')}
              <Link
                href={learnMoreUrl}
                sx={{ textDecoration: 'underline', marginLeft: '0.3rem', fontWeight: '600' }}
              >
                {t('screens.transactions.learnMore')}
              </Link>
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: '8px', mb: 3 }}>
          {filterButtons.map((filter) => {
            if (filter === FAVOURITES) {
              return (
                <Button
                  key={FAVOURITES}
                  variant="outlined"
                  color={activeFilter === FAVOURITES ? 'primary' : 'greyed'}
                  startIcon={<StarIcon />}
                  onClick={() => setActiveFilter(FAVOURITES)}
                >
                  {t('screens.dashboarding.favourite')}
                </Button>
              );
            }

            if (filter === ALL) {
              return (
                <Button
                  key={ALL}
                  variant="outlined"
                  color={activeFilter === ALL ? 'primary' : 'greyed'}
                  onClick={() => setActiveFilter(ALL)}
                >
                  {t('screens.dataDownloader.allReports')}
                </Button>
              );
            }

            return null;
          })}

          {categories.map((category: ReportCategory) => (
            <Button
              key={category.displayKey}
              variant="outlined"
              color={
                activeFilter === category.displayKey ? 'primary' : 'greyed'
              }
              onClick={() => setActiveFilter(category.displayKey)}
            >
              {t(`screens.dataDownloader.reportCategory.${category.displayKey}`)}
            </Button>
          ))}
        </Box>
        <ReportOverviewTable reports={filteredReports} reportTypes={reportOverview}
          onNullFavorites={() => {
            setActiveFilter(ALL);
          }}
            showNoFavourites={showNoFavourites}
          refreshData={refetch} />
      </Box>
    </>
  );
};

export default ReportOverview;
